import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts'
import styled from 'styled-components'
import { SPACING, TYPOGRAPHY, FONT_WEIGHT } from '../../../../styles/theme'
import { INSIGHT_COLOR_YES, INSIGHT_COLOR_NO, INSIGHT_TOOLTIP_STYLE } from './insightColors'

interface YesNoChartProps {
  answers: boolean[]
  isPrinting: boolean
}

const Wrapper = styled.div`
  position: relative;
`

const CenterLabel = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  pointer-events: none;
`

const CenterPct = styled.div`
  font-size: ${TYPOGRAPHY.h4.fontSize};
  font-weight: ${FONT_WEIGHT.bold};
  color: var(--color-text-primary);
  line-height: 1;
`

const CenterWord = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  margin-top: 2px;
`

const Legend = styled.div`
  display: flex;
  justify-content: center;
  gap: ${SPACING.xl};
  margin-top: ${SPACING.md};
  font-size: ${TYPOGRAPHY.caption.fontSize};
`

const LegendItem = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: ${SPACING.xs};

  &::before {
    content: '';
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${(p) => p.$color};
  }
`

const NoData = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  text-align: center;
  padding: ${SPACING.xl} 0;
`

export function YesNoChart({ answers, isPrinting }: YesNoChartProps) {
  if (answers.length === 0) {
    return <NoData>No responses yet</NoData>
  }

  const yesCount = answers.filter((a) => a === true).length
  const noCount = answers.filter((a) => a === false).length
  const total = yesCount + noCount
  const yesPct = Math.round((yesCount / total) * 100)
  const noPct = 100 - yesPct
  const dominant = yesPct >= 50 ? { pct: yesPct, label: 'Yes' } : { pct: noPct, label: 'No' }

  const data = [
    { name: 'Yes', value: yesCount },
    { name: 'No', value: noCount },
  ]

  const COLORS_MAP: Record<string, string> = { Yes: INSIGHT_COLOR_YES, No: INSIGHT_COLOR_NO }

  return (
    <>
      <Wrapper>
        <ResponsiveContainer width="100%" height={160}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={72}
              dataKey="value"
              startAngle={90}
              endAngle={-270}
              isAnimationActive={!isPrinting}
            >
              {data.map((entry) => (
                <Cell key={entry.name} fill={COLORS_MAP[entry.name]} />
              ))}
            </Pie>
            {!isPrinting && (
              <Tooltip
                {...INSIGHT_TOOLTIP_STYLE}
                formatter={(val: number, name: string) => [
                  `${val} (${Math.round((val / total) * 100)}%)`,
                  name,
                ]}
              />
            )}
          </PieChart>
        </ResponsiveContainer>
        <CenterLabel>
          <CenterPct>{dominant.pct}%</CenterPct>
          <CenterWord>{dominant.label}</CenterWord>
        </CenterLabel>
      </Wrapper>
      <Legend>
        <LegendItem $color={INSIGHT_COLOR_YES}>
          Yes — {yesPct}% ({yesCount})
        </LegendItem>
        <LegendItem $color={INSIGHT_COLOR_NO}>
          No — {noPct}% ({noCount})
        </LegendItem>
      </Legend>
    </>
  )
}
